import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollText, Plus, X, AlertCircle } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import type { AuditListItem, AuditStatus, AuditType } from '../../../types/whs';
import {
  Badge, AUDIT_TYPE_LABEL, AUDIT_STATUS_LABEL, AUDIT_STATUS_CLASS,
} from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

const STATUS_FILTERS: Array<{ value: '' | AuditStatus; label: string }> = [
  { value: '', label: 'All' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

const AUDIT_TYPES: AuditType[] = ['internal', 'external', 'compliance', 'management_system'];

function userName(u: UserData): string {
  const full = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return full || u.username;
}

function auditorLabel(a: AuditListItem, users: UserData[]): string {
  if (a.auditor_name) return a.auditor_name;
  if (a.auditor_id != null) {
    const u = users.find((x) => x.id === a.auditor_id);
    if (u) return userName(u);
  }
  return '—';
}

function WhsAuditsInner() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();

  const [audits, setAudits] = useState<AuditListItem[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | AuditStatus>('');

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [auditType, setAuditType] = useState<AuditType>('internal');
  const [standardRef, setStandardRef] = useState('ISO 45001:2018');
  const [scope, setScope] = useState('');
  const [auditorId, setAuditorId] = useState('');
  const [auditorName, setAuditorName] = useState('');
  const [auditDate, setAuditDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.listAudits(statusFilter ? { status: statusFilter } : {});
    if (res.success) setAudits(res.data);
    else setError(res.error.message);
    setLoading(false);
  }, [statusFilter]);

  const loadUsers = useCallback(async () => {
    const res = await userService.getUsers();
    if (res.success) setUsers(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openModal = () => {
    setFormError(null);
    setTitle('');
    setAuditType('internal');
    setStandardRef('ISO 45001:2018');
    setScope('');
    setAuditorId('');
    setAuditorName('');
    setAuditDate('');
    setShowModal(true);
  };

  const handleCreate = async () => {
    if (!title.trim()) { setFormError('Title is required.'); return; }
    setSaving(true);
    setFormError(null);
    const payload: Record<string, unknown> = {
      title: title.trim(),
      audit_type: auditType,
    };
    if (standardRef.trim()) payload.standard_ref = standardRef.trim();
    if (scope.trim()) payload.scope = scope.trim();
    if (auditorId) payload.auditor_id = Number(auditorId);
    if (auditorName.trim()) payload.auditor_name = auditorName.trim();
    if (auditDate) payload.audit_date = auditDate;
    const res = await whsService.createAudit(payload);
    setSaving(false);
    if (res.success) {
      setShowModal(false);
      navigate(`/hr/whs/audits/${res.data.id}`);
    } else {
      setFormError(res.error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Audits"
        subtitle="Internal & external audits with findings and non-conformities"
        icon={<ScrollText className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value || 'all'}
                onClick={() => setStatusFilter(f.value)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  statusFilter === f.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button onClick={openModal} size="sm">
            <Plus className="h-4 w-4" /> New audit
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : audits.length === 0 ? (
          <div className="rounded-xl border border-neutral-200 bg-white p-10 text-center">
            <ScrollText className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-sm text-neutral-500">No audits found.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Standard</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Auditor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wide">Findings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {audits.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/hr/whs/audits/${a.id}`)}
                    className="cursor-pointer hover:bg-neutral-50"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-neutral-900">{a.title}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600">{AUDIT_TYPE_LABEL[a.audit_type]}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600">{a.standard_ref || '—'}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600">{auditorLabel(a, users)}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600">
                      {a.audit_date ? formatLocalDateInTz(a.audit_date, orgTz) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge label={AUDIT_STATUS_LABEL[a.status]} className={AUDIT_STATUS_CLASS[a.status]} />
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-neutral-700">
                      {a.finding_count}
                      {a.open_finding_count > 0 ? <span className="ml-1 font-semibold text-red-600">({a.open_finding_count} open)</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-neutral-900">New audit</h2>
              <button onClick={() => setShowModal(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              {formError && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="e.g. Q3 internal WHS audit"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Audit type</label>
                <select
                  value={auditType}
                  onChange={(e) => setAuditType(e.target.value as AuditType)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {AUDIT_TYPES.map((t) => (
                    <option key={t} value={t}>{AUDIT_TYPE_LABEL[t]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Standard reference</label>
                <input
                  value={standardRef}
                  onChange={(e) => setStandardRef(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="e.g. ISO 45001:2018"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Scope</label>
                <textarea
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="What this audit covers…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Auditor (internal)</label>
                <select
                  value={auditorId}
                  onChange={(e) => setAuditorId(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{userName(u)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Auditor name (external)</label>
                <input
                  value={auditorName}
                  onChange={(e) => setAuditorName(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="For an external auditor"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Audit date</label>
                <input
                  type="date"
                  value={auditDate}
                  onChange={(e) => setAuditDate(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4">
              <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} isLoading={saving}>Create</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WhsAudits() {
  return <WhsGate><WhsAuditsInner /></WhsGate>;
}
